import React, {ChangeEvent, useEffect, useMemo, useState} from 'react';

import {format} from 'date-fns';
import {useGate, useUnit} from 'effector-react';
import debounce from 'lodash/debounce';
import {useTranslation} from 'react-i18next';
import {generatePath, useParams} from 'react-router-dom';

import {ROUTES} from '@app/routing';
import {DATE_VIEW_PATTERN} from '@consts/date';
import {INVOICE_COLORS, STATUS_COLOR_LIST} from '@consts/statuses';
import {InvoiceRawRow} from '@entities/invoices/types';
import {taxpayerDetailsModel, TaxpayerInvoicesGate} from '@entities/taxpayers/detail-model';
import {GridFieldConfig} from '@shared/types/grid';
import {Input} from '@shared/ui/Input';
import {Link} from '@shared/ui/Link';
import {StatusWrapper} from '@shared/ui/PageComponents';
import {mapTableColumns, Table} from '@shared/ui/Table2.0';

import {ActionGroup, Controls, FilterButton, SearchBox} from '../styled';

import {formatAmount} from './helpers';

type InvoiceView = 'seller' | 'buyer';

const invoiceViews: InvoiceView[] = ['seller', 'buyer'];

const config = (t: any): GridFieldConfig[] => [
    {id: 'tin', header: t('page_taxpayer.invoices.tin'), width: 180},
    {id: 'irn', header: t('page_invoices.table.serial_number'), minWidth: 220},
    {id: 'invoiceDate', header: t('page_invoices.table.date'), width: 150, isSortable: true},
    {id: 'invoiceId', header: t('page_invoices.table.id'), minWidth: 260},
    {id: 'parentUuid', header: t('page_invoices.table.parent_number'), minWidth: 240},
    {id: 'amountWithoutVat', header: t('page_invoices.table.sum_without_taxes'), minWidth: 220},
    {id: 'vatSum', header: t('page_invoices.table.tax_sum'), minWidth: 160, isSortable: true},
    {id: 'amountWithVat', header: t('page_invoices.table.sum'), minWidth: 220, isSortable: true},
    {id: 'docType', header: t('page_invoices.table.doc_type'), width: 160},
    {id: 'acceptStatus', header: t('page_invoices.table.accept_status'), width: 160},
];

const taxpayerLink = (tin: string) => ({
    pathname: generatePath(ROUTES.TAXPAYER_DETAILS, {tin}),
    state: {from: location.pathname},
});

const invoiceLink = (id: string, tin?: string) => ({
    pathname: generatePath(ROUTES.INVOICE_DETAILS, {id}),
    ...(tin && {search: `?tin=${tin}`}),
    state: {from: location.pathname},
});

export const InvoicesTab = () => {
    const {tin} = useParams<{tin: string}>();
    const {t} = useTranslation();
    const taxpayer = useUnit(taxpayerDetailsModel);
    const [search, setSearch] = useState(taxpayer.state.invoiceSearch);

    useGate(TaxpayerInvoicesGate, {tin});

    const debounced = useMemo(
        () => debounce((value: string) => taxpayer.invoiceSearchChanged(value), 700),
        [],
    );

    useEffect(() => {
        if (search === taxpayer.state.invoiceSearch) return undefined;

        debounced(search);
        return debounced.cancel;
    }, [search, taxpayer.state.invoiceSearch, debounced]);

    const data = (taxpayer.state.invoices?.data ?? []).map((row: InvoiceRawRow) => {
        const counterpartyTin =
            taxpayer.state.invoiceView === 'seller' ? row.buyerTaxNumber : row.sellerTaxNumber;

        return {
            ...row,
            tin: <Link to={taxpayerLink(counterpartyTin)}>{counterpartyTin}</Link>,
            irn: <Link to={invoiceLink(row.invoiceId, counterpartyTin)}>{row.irn}</Link>,
            invoiceId: (
                <Link to={invoiceLink(row.invoiceId, counterpartyTin)}>{row.invoiceId}</Link>
            ),
            parentUuid: row.parentUuid ? (
                <Link to={invoiceLink(row.parentUuid, counterpartyTin)}>{row.parentUuid}</Link>
            ) : (
                '—'
            ),
            invoiceDate: row.invoiceDate
                ? format(new Date(row.invoiceDate), DATE_VIEW_PATTERN)
                : '—',
            amountWithoutVat: formatAmount(row.amountWithoutVat),
            vatSum: formatAmount(row.vatSum),
            amountWithVat: formatAmount(row.amountWithVat),
            docType: (
                <StatusWrapper
                    color={INVOICE_COLORS.DOCUMENT[row.docType] ?? STATUS_COLOR_LIST.GRAY}
                >
                    {t(`terms.invoice.type.${row.docType}`)}
                </StatusWrapper>
            ),
            acceptStatus: row.acceptStatus ? (
                <StatusWrapper
                    color={INVOICE_COLORS.ACCEPT[row.acceptStatus] ?? STATUS_COLOR_LIST.GRAY}
                >
                    {t(`terms.invoice.status.${row.acceptStatus}`)}
                </StatusWrapper>
            ) : (
                '—'
            ),
        };
    });

    return (
        <>
            <Controls>
                <ActionGroup>
                    {invoiceViews.map((view) => (
                        <FilterButton
                            key={view}
                            type="button"
                            isActive={taxpayer.state.invoiceView === view}
                            onClick={() => taxpayer.invoiceViewChanged(view)}
                        >
                            {t(
                                `page_taxpayer.invoices.${
                                    view === 'seller' ? 'outcome' : 'income'
                                }`,
                            )}
                        </FilterButton>
                    ))}
                </ActionGroup>
                <SearchBox>
                    <Input
                        value={search}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
                        placeholder={t('page_taxpayer.invoices.search')}
                    />
                </SearchBox>
            </Controls>
            <Table
                columns={mapTableColumns(
                    config(t),
                    undefined,
                    undefined,
                    undefined,
                    taxpayer.state.invoiceSortings,
                    (items) =>
                        taxpayer.invoiceSortingsChanged(
                            items.reduce((acc, item) => ({...acc, [item.id]: item.value}), {}),
                        ),
                )}
                data={data}
                isLoading={taxpayer.state.pending}
                hasNext={Boolean(taxpayer.state.invoices?.nextPageRef)}
                pageIndex={taxpayer.state.invoicePage}
                onPageChange={taxpayer.invoicePageChanged}
                onPageSizeChange={taxpayer.invoicePageSizeChanged}
            />
        </>
    );
};
